import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AddEmployee from "@/pages/AddEmployee";
import AddShift from "@/pages/AddShift";
import AddEvent from "@/pages/AddEvent";
import WorkSchedule from "@/pages/WorkSchedule";
import CompanySchedule from "@/pages/CompanySchedule";
import Reports from "@/pages/Reports";
import EmployeeInfo from "@/pages/EmployeeInfo";
import EmployeeList from "@/pages/EmployeeList";
import Settings from "@/pages/Settings";
import Marketplace from "@/pages/Marketplace";
import OpenShifts from "@/pages/OpenShifts";
import ShiftBids from "@/pages/ShiftBids";
import RecurringShifts from "@/pages/RecurringShifts";
import TimeOff from "@/pages/TimeOff";
import Analytics from "@/pages/Analytics";
import Availability from "@/pages/Availability";
import RecentlyDeleted from "@/pages/RecentlyDeleted";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const WS_URL = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");

// Configure axios defaults - use Authorization header (not cookies) for auth
// withCredentials is NOT used because K8s ingress sets Access-Control-Allow-Origin: *
// which browsers reject with credentials mode

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Helper function to format API errors
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [scheduleUpdate, setScheduleUpdate] = useState(0);
  const wsRef = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/auth/me`, { headers });
      setUser(response.data);
      fetchUnreadCount();
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUnreadCount = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/notifications/unread-count`, { headers });
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!user?.id || wsRef.current) return;
    
    try {
      const ws = new WebSocket(`${WS_URL}/api/ws/${user.id}`);
      
      ws.onopen = () => {
        console.log("WebSocket connected");
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "notification") {
            // Show toast notification
            toast.info(data.data.title, {
              description: data.data.message
            });
            fetchUnreadCount();
          } else if (data.type === "schedule_update") {
            // Trigger schedule refresh
            setScheduleUpdate(prev => prev + 1);
            toast.info("Schedule Updated", {
              description: data.data.event_name ? `Event "${data.data.event_name}" was ${data.data.action}` : "The schedule has been updated"
            });
          }
        } catch (e) {
          console.error("Error parsing WebSocket message:", e);
        }
      };
      
      ws.onclose = () => {
        console.log("WebSocket disconnected");
        wsRef.current = null;
        // Reconnect after 5 seconds
        setTimeout(() => {
          if (user?.id) connectWebSocket();
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error("WebSocket connection error:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Connect WebSocket when user logs in
  useEffect(() => {
    if (user?.id) {
      connectWebSocket();
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user?.id, connectWebSocket]);

  // Poll for notifications every 30 seconds (backup)
  useEffect(() => {
    if (user) {
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    if (response.data.token) {
      setToken(response.data.token);
      localStorage.setItem("token", response.data.token);
    }
    fetchUnreadCount();
    return response.data;
  };

  const logout = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setToken(null);
    setUnreadNotifications(0);
    localStorage.removeItem("token");
  };

  const getAuthHeaders = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAdmin: user?.role === "admin",
    isSupervisor: user?.role === "supervisor",
    canManage: user?.role === "admin" || user?.role === "supervisor",
    token,
    getAuthHeaders,
    unreadNotifications,
    refreshNotifications: fetchUnreadCount,
    scheduleUpdate  // Used to trigger schedule refresh in components
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Force Password Change Component
const ForcePasswordChange = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const [username, setUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim()) {
      setError("Please create a username");
      return;
    }
    if (username.trim().length < 3) {
      setError("Username must be at least 3 characters");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPassword, username: username.trim() }, { headers: getAuthHeaders() });
      window.location.reload();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to set up account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="force-password-change">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto bg-[#F0EAEB] rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-[#6A1E2E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          </div>
          <h2 className="text-2xl font-bold font-heading text-[#1A1A1A]">Set Up Your Account</h2>
          <p className="text-[#666666] mt-1">Create a username and set your password to get started</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm">{error}</div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1A1A1A]">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full h-11 px-3 border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#6A1E2E]"
              placeholder="Choose a username"
              required
              data-testid="username-input"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1A1A1A]">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full h-11 px-3 border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#6A1E2E]"
              placeholder="Enter new password"
              required
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#1A1A1A]">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full h-11 px-3 border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#6A1E2E]"
              placeholder="Confirm new password"
              required
              data-testid="confirm-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full h-11 bg-[#6A1E2E] hover:bg-[#521422] text-white font-medium rounded-sm disabled:opacity-50"
            data-testid="change-password-submit"
          >
            {saving ? "Setting up..." : "Complete Setup"}
          </button>
          <button
            type="button"
            onClick={logout}
            className="w-full h-9 text-sm text-[#666666] hover:text-[#1A1A1A]"
          >
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, canManage } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]" data-testid="loading-screen">
        <div className="animate-pulse">
          <div className="w-16 h-16 border-4 border-[#6A1E2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }

  if (adminOnly && !canManage) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="add-employee"
              element={
                <ProtectedRoute adminOnly>
                  <AddEmployee />
                </ProtectedRoute>
              }
            />
            <Route
              path="add-shift"
              element={
                <ProtectedRoute adminOnly>
                  <AddShift />
                </ProtectedRoute>
              }
            />
            <Route
              path="add-event"
              element={
                <ProtectedRoute adminOnly>
                  <AddEvent />
                </ProtectedRoute>
              }
            />
            <Route path="work-schedule" element={<WorkSchedule />} />
            <Route path="company-schedule" element={<CompanySchedule />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="open-shifts" element={<OpenShifts />} />
            <Route path="shift-bids" element={<ShiftBids />} />
            <Route path="time-off" element={<TimeOff />} />
            <Route path="availability" element={<Availability />} />
            <Route
              path="recurring-shifts"
              element={
                <ProtectedRoute adminOnly>
                  <RecurringShifts />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedRoute adminOnly>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute adminOnly>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route path="info" element={<EmployeeInfo />} />
            <Route path="employee-list" element={<EmployeeList />} />
            <Route
              path="settings"
              element={
                <ProtectedRoute adminOnly>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="recently-deleted"
              element={
                <ProtectedRoute adminOnly>
                  <RecentlyDeleted />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
